<script>
import { required, maxLength, helpers } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'
import SocialCreateProfile from './SocialCreateProfile'

const alphaNumHyphenUnderscoreValidator = helpers.regex('alphaNumAndDot', /^[-a-zA-Z0-9_]+$/i)

const NAMES_MAX_LENGTH = 20
const NICKNAME_MAX_LENGTH = 20
const LOCATION_MAX_LENGTH = 50

export default {
  name: 'social-edit-profile',
  extends: SocialCreateProfile,
  props: {
    profileNickname: String
  },
  async created () {
    await this.fetchMyProfile(this.profileNickname)

    if (!this.profile) {
      this.$router.replace({ name: 'social' })
      return
    }
    this.pictureSrc = this.profile.picture
    if (this.profile.background) {
      this.backgroundSrc = this.profile.background
    }
    this.firstName = this.profile.firstName
    this.lastName = this.profile.lastName
    this.nickname = this.profile.nickname
    this.textLocation = this.profile.textLocation
    this.category = this.categories.find(catObj => catObj.value === this.profile.category) || this.categories[0]
    this.isActive = this.profile.isActive
    this.isPublic = this.profile.isPublic
    this.showFollowers = this.profile.showFollowers
    this.isChatActive = this.profile.isChatActive
  },
  computed: {
    ...mapGetters({
      getProfile: 'entities/socialProfiles/getProfile'
    }),
    profile () {
      return this.getProfile(this.profileNickname)
    },
    title () {
      return this.$t('socialProfile.editTitle')
    },
    buttonText () {
      return this.$t('buttonConfirm')
    }
  },
  validations: {
    firstName: { required, maxLength: maxLength(NAMES_MAX_LENGTH) },
    lastName: { maxLength: maxLength(NAMES_MAX_LENGTH) },
    nickname: {
      required,
      maxLength: maxLength(NICKNAME_MAX_LENGTH),
      alphaNumHyphenUnderscoreValidator,
      uniq () {
        return !this.errorUniqNickname
      }
    },
    textLocation: { required, maxLength: maxLength(LOCATION_MAX_LENGTH) },
    pictureFile: {}
  },
  methods: {
    ...mapActions({
      fetchMyProfile: 'entities/socialProfiles/fetchMyProfile',
      updateProfile: 'entities/socialProfiles/updateProfile'
    }),
    async onSubmit () {
      this.$v.$touch()
      if (this.loading || this.$v.$invalid) return
      const data = {
        firstName: this.firstName,
        lastName: this.lastName,
        nickname: this.nickname,
        textLocation: this.textLocation,
        category: this.category.value,
        isActive: this.isActive,
        isPublic: this.isPublic,
        showFollowers: this.showFollowers,
        isChatActive: this.isChatActive
      }
      if (this.textLocation !== this.profile.textLocation) {
        const location = await api.location.coordinatesByAddress(this.textLocation)
        data.location = JSON.stringify(location)
      }
      if (this.pictureFile) {
        data.picture = this.pictureFile
      }
      if (this.backgroundFile) {
        data.background = this.backgroundFile
      }
      this.loading = true
      try {
        await this.updateProfile({ nickname: this.profileNickname, data })
      } catch (e) {
        const { nickname } = e.response.data
        if (nickname) {
          this.errorUniqNickname = true
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style module lang="scss">
.container {
  padding: 8px 20px;
  overflow-x: hidden;
  overflow-y: scroll;
  flex: 1;
  .button {
    margin: 0 auto;
  }
}
.title {
  font-size: 18px;
  font-weight: 500;
  color: $text-success;
  margin: 0;
  margin-bottom: 16px;
  text-align: center;
}
.picture,
.background {
  background-color: $main-bg-light;
  border: 4px solid $border-color;
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  transition: all 0.2s;
  &:hover {
    border-color: $text-mid;
    .pencilIcon {
      color: $text-mid;
    }
  }
}
.picture {
  width: 110px;
  height: 110px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  &Error {
    border-color: $text-error;
  }
}
.background {
  width: 240px;
  height: 110px;
  border-radius: 5px;
  top: -65px;
}
.image {
  width: 100%;
}
.pencilIcon {
  font-size: 16px;
  color: $text-light;
  position: absolute;
  transition: all 0.7s;
}
.pencilIconPicture {
  position: absolute;
  top: 8px;
}
.pencilIconBackground {
  position: absolute;
  bottom: 8px;
  right: 8px;
}
.extended {
  width: 100%;
  text-align: center;
  padding: 10px 0;
  background-color: rgba($main-bg-colored, 0.15);
  text-transform: uppercase;
  margin-bottom: 12px;
}
.extendedBlock {
  padding: 12px 0;
}
.label {
  color: $text-colored;
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 4px;
}
.switchMargin {
  margin-bottom: 2px;
}
</style>
